import React, { useRef, useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import client from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote,
  Minus, Link2, Image as ImageIcon, Undo2, Redo2, Loader2,
} from "lucide-react";

const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];
const MAX_BYTES = 8 * 1024 * 1024; // 8MB

// Downscale large images client-side (compression) before upload.
async function compressImage(file) {
  if (file.type === "image/gif") return file; // keep animation
  try {
    const bitmap = await createImageBitmap(file);
    const maxW = 1600;
    if (bitmap.width <= maxW) return file;
    const scale = maxW / bitmap.width;
    const canvas = document.createElement("canvas");
    canvas.width = maxW;
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise((res) => canvas.toBlob(res, type, 0.85));
    return blob ? new File([blob], file.name, { type }) : file;
  } catch {
    return file;
  }
}

export function RichEditor({ value, onChange, placeholder = "Write your story…" }) {
  const fileRef = useRef(null);
  const uploadingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Image.configure({ HTMLAttributes: { loading: "lazy", class: "article-inline-img" } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noreferrer", target: "_blank" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: "tiptap-body article-prose focus:outline-none", "data-testid": "rich-editor-body" },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items || [];
        for (const it of items) {
          if (it.type.startsWith("image/")) {
            const f = it.getAsFile();
            if (f) { uploadAndInsert(f); return true; }
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const f = event.dataTransfer?.files?.[0];
        if (f && f.type.startsWith("image/")) { event.preventDefault(); uploadAndInsert(f); return true; }
        return false;
      },
    },
  });

  // keep editor in sync when loading an existing article into the form
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const uploadAndInsert = useCallback(async (file) => {
    if (!editor) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Only PNG, JPG, WEBP or GIF images are allowed."); return; }
    if (file.size > MAX_BYTES) { toast.error("Image is too large (max 8MB)."); return; }
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    const t = toast.loading("Uploading image…");
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const src = data.url;
      const alt = window.prompt("Alt text (for accessibility & SEO):", "") || "";
      editor.chain().focus().setImage({ src, alt }).run();
      toast.success("Image inserted", { id: t });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed", { id: t });
    } finally {
      uploadingRef.current = false;
    }
  }, [editor]);

  const onPick = (e) => { const f = e.target.files?.[0]; if (f) uploadAndInsert(f); if (fileRef.current) fileRef.current.value = ""; };

  const setLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) return null;

  const Btn = ({ on, active, label, testid, children }) => (
    <button type="button" data-testid={testid} title={label} aria-label={label} onClick={on}
      className={`grid place-items-center w-9 h-9 rounded-lg transition-colors ${active ? "bg-[#ccff00] text-black" : "text-zinc-300 hover:bg-white/10"}`}>
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border border-white/12 bg-[#0a0a0a] overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-white/10 sticky top-0 bg-[#0d0d0d] z-10">
        <Btn testid="rte-bold" on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Bold"><Bold className="w-4 h-4" /></Btn>
        <Btn testid="rte-italic" on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Italic"><Italic className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-h2" on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Heading 2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn testid="rte-h3" on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Heading 3"><Heading3 className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-ul" on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Bullet list"><List className="w-4 h-4" /></Btn>
        <Btn testid="rte-ol" on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numbered list"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn testid="rte-quote" on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Quote"><Quote className="w-4 h-4" /></Btn>
        <Btn testid="rte-hr" on={() => editor.chain().focus().setHorizontalRule().run()} label="Divider"><Minus className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-link" on={setLink} active={editor.isActive("link")} label="Link"><Link2 className="w-4 h-4" /></Btn>
        <Btn testid="rte-image" on={() => fileRef.current?.click()} label="Insert image">{uploadingRef.current ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}</Btn>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-undo" on={() => editor.chain().focus().undo().run()} label="Undo"><Undo2 className="w-4 h-4" /></Btn>
        <Btn testid="rte-redo" on={() => editor.chain().focus().redo().run()} label="Redo"><Redo2 className="w-4 h-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="rte-image-input" />
      </div>
      <EditorContent editor={editor} className="p-4 sm:p-5 min-h-[320px] max-h-[640px] overflow-y-auto" />
    </div>
  );
}
